import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCountdown, useTranslator } from '@ianesthesie-kit/core';
import {
  AuthService,
  CommunicationChannel,
  isErrorDetails
} from '@ianesthesie-kit/services';
import { useCommunicationChannelProps, useAppDispatch } from '@/hooks';
import { SentryService } from '@ianesthesie-kit/sentry';
import { pingCurrentUser, useSnackbar } from '@ianesthesie-kit/redux';
import { MAX_OTP_RETRIES, OTP_TTL } from '@/config';

export type UseOtpResult = {
  otp: string;
  onChange: (opt: string) => void;
  isOtpPending: boolean;
  otpError: any;
  disabled: boolean;
  countdown: number;
  isCountdownRunning: boolean;
  cancelCountdown: () => void;
  restartCountdown: () => void;
  requestOtp: (
    phoneOrEmail: string,
    showSnackbarOnSent?: boolean
  ) => Promise<void>;
  verifyOtp: (code: string) => Promise<boolean>;
  isSubmitting: boolean;
};

export const useOtp = (type: CommunicationChannel): UseOtpResult => {
  const typeSpecific = useCommunicationChannelProps(type);
  const [otp, setOtp] = useState('');
  const [otpPending, setOtpPending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [retriesDone, setRetriesDone] = useState(0);
  const disabled = retriesDone >= MAX_OTP_RETRIES;
  const [error, setError] = useState<any>();
  const { cancel, isRunning, restart, countdown } = useCountdown(OTP_TTL, {
    intervalMs: 500,
    autoStart: false
  });
  const { showSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const translate = useTranslator();

  useEffect(() => {
    if (disabled && isRunning) {
      cancel();
    }
  }, [cancel, disabled, isRunning]);

  const onChange = useCallback((newOtp: string) => {
    setOtp(newOtp);
    if (newOtp.length) {
      setError(undefined);
    }
  }, []);

  const requestOtp = useCallback(
    (phoneOrEmail: string, showSnackbarOnSent = true) => {
      if (isRunning) {
        throw new Error('Otp already requested');
      }

      if (!phoneOrEmail) {
        throw new Error('No target phone number or email present');
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return typeSpecific.sendVerificationCodeFn
        .call(AuthService.getInstance(), {
          [type]: phoneOrEmail
        })
        .then(() => {
          restart();
          setError(undefined);
          setRetriesDone(0);
          setOtp('');
          setOtpPending(true);
          if (showSnackbarOnSent) {
            showSnackbar({
              severity: 'success',
              message: translate(typeSpecific.requestSentMessage),
              position: {
                horizontal: 'center',
                vertical: 'top'
              }
            });
          }
        });
    },
    [
      isRunning,
      restart,
      showSnackbar,
      translate,
      type,
      typeSpecific.requestSentMessage,
      typeSpecific.sendVerificationCodeFn
    ]
  );

  const verifyOtp = useCallback(
    (code: string) => {
      if (disabled) {
        throw new Error('Otp verification is suspended');
      }
      if (!code) {
        throw new Error('No otp present');
      }

      setError(undefined);
      setIsSubmitting(true);

      return dispatch(typeSpecific.verifyActionCreator({ code }))
        .then(() => dispatch(pingCurrentUser()))
        .then(() => {
          cancel();
          setOtpPending(false);
          showSnackbar({
            severity: 'success',
            message: translate(typeSpecific.verificationSuccessMessage),
            position: {
              horizontal: 'center',
              vertical: 'top'
            }
          });

          return true;
        })
        .catch((err) => {
          setError(err);
          if (!isErrorDetails(err) || err.status >= 500) {
            cancel();
            SentryService.getInstance().captureException(err);
          }
          return false;
        })
        .finally(() => {
          setOtp('');
          setRetriesDone((prev) => prev + 1);
          setIsSubmitting(false);
        });
    },
    [cancel, disabled, dispatch, showSnackbar, translate, typeSpecific]
  );

  return useMemo(
    () => ({
      otp,
      onChange,
      otpError: error,
      isOtpPending: otpPending,
      countdown,
      isCountdownRunning: isRunning,
      cancelCountdown: cancel,
      restartCountdown: restart,
      requestOtp,
      verifyOtp,
      disabled,
      isSubmitting
    }),
    [
      otp,
      error,
      onChange,
      otpPending,
      countdown,
      isRunning,
      cancel,
      restart,
      requestOtp,
      verifyOtp,
      disabled,
      isSubmitting
    ]
  );
};
